import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../common/CommonLoader/CommonLoader";
import AppButton from "../../common/AppButton";
import { useStockItems } from "../../../hooks/api/education/useStockItem";
import {
  useApprovePurchaseOrder,
  useCreateGrn,
  useCreatePurchaseOrder,
  usePostGrn,
  usePurchaseOrders
} from "../../../hooks/api/education/useProcurement";

const ProcurementPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const {
    data: orders = [],
    isPending,
    refetch
  } = usePurchaseOrders({ size: 50 });
  const { data: stockItems = [] } = useStockItems({ size: 100 });
  const createPo = useCreatePurchaseOrder();
  const approvePo = useApprovePurchaseOrder();
  const createGrn = useCreateGrn();
  const postGrn = usePostGrn();
  const [open, setOpen] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [stockItemId, setStockItemId] = useState("");
  const [quantity, setQuantity] = useState("1");

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-procurement", "Loading procurement...")}
      />
    );
  }

  const run = async (id: string, action: "approve" | "grn" | "post") => {
    if (action === "approve") await approvePo.mutateAsync({ orderId: id });
    if (action === "grn") await createGrn.mutateAsync({ purchaseOrderId: id });
    if (action === "post") await postGrn.mutateAsync({ orderId: id });
    refetch();
  };

  return (
    <Box data-testid="procurement-page" sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">
          {t("procurement-title", "Procurement")}
        </Typography>
        <AppButton onClick={() => setOpen(true)}>
          {t("new-purchase-order", "New PO")}
        </AppButton>
      </Box>

      {orders.length === 0 ? (
        <Typography color="text.secondary">
          {t("procurement-empty", "No purchase orders yet.")}
        </Typography>
      ) : (
        orders.map((order) => (
          <Box
            key={order.id}
            sx={{ py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Typography fontWeight={600}>
              {order.orderNumber} · {order.procurementStatus}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
              {order.procurementStatus === "DRAFT" && (
                <AppButton
                  size="small"
                  onClick={() => order.id && run(order.id, "approve")}
                >
                  {t("approve", "Approve")}
                </AppButton>
              )}
              {order.procurementStatus === "APPROVED" && (
                <AppButton
                  size="small"
                  onClick={async () => {
                    if (!order.id) return;
                    const grnId = await createGrn.mutateAsync({
                      purchaseOrderId: order.id
                    });
                    await postGrn.mutateAsync({ orderId: grnId });
                    refetch();
                  }}
                >
                  {t("receive-stock", "Receive stock (GRN)")}
                </AppButton>
              )}
            </Box>
          </Box>
        ))
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("new-purchase-order", "New PO")}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label={t("supplier-id", "Supplier ID")}
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            fullWidth
          />
          <TextField
            select
            label={t("stock-item", "Stock item")}
            value={stockItemId}
            onChange={(e) => setStockItemId(e.target.value)}
            fullWidth
          >
            {stockItems.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t("quantity", "Quantity")}
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <AppButton variant="secondary" onClick={() => setOpen(false)}>
            {t("cancel", "Cancel")}
          </AppButton>
          <AppButton
            disabled={!supplierId || !stockItemId || createPo.isPending}
            onClick={async () => {
              const selected = stockItems.find((s) => s.id === stockItemId);
              await createPo.mutateAsync({
                supplierId,
                lines: [
                  {
                    stockItemId,
                    stockItemName: selected?.name,
                    quantity: Number(quantity),
                    unit: selected?.baseUnit
                  }
                ]
              });
              setOpen(false);
              refetch();
            }}
          >
            {t("create", "Create")}
          </AppButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcurementPage;
